"use client";
/**
 * LoginModalProvider — 基于新 auth 模块的精简版
 *
 * 职责：仅管理登录弹窗的可见性 + 触发登录后跳转/回调
 * 登录状态由 AuthOrchestrator 管理（features/auth）
 *
 * 旧版保留为 LoginModalProvider.legacy.tsx，供回退参考
 */
import {
  type Context,
  createContext,
  useCallback,
  useRef,
  useState,
} from "react";
import log from "logbeacon";
import { useMemoizedFn } from "ahooks";
import { useAuth } from "@/src/features/auth";
import type { AuthProvider } from "@/src/features/auth";
import { useShellRouter } from "@/src/hooks";
import { EmailLoginModal } from "@/src/components/EmailLogin";
import {
  type LoginOpenOptions,
  type LoginPostAction,
  type ILoginModalContext,
  type ILoginProviderProps,
} from "./types";
import { LoginStore, rootStore } from "@store";
import type { Web3LoginSupportedType } from "@/src/features/auth/compat/sdk-types";
import { useOnboardingEntryGate } from "@/src/hooks/onboarding";

export { SESSION_JUST_LOGGED_OUT_KEY } from "./authSessionSyncRefs";

export const LoginModalContext: Context<ILoginModalContext> =
  createContext<ILoginModalContext>({});

const PROVIDER_MAP: Partial<Record<Web3LoginSupportedType, AuthProvider>> = {
  APPLE: "apple",
  GOOGLE: "google",
  TWITTER: "twitter",
};

export const LoginModalProvider = ({ children }: ILoginProviderProps) => {
  const [loginModalVisible, setLoginModalVisible] = useState(false);
  const [hideCloseButton, setHideCloseButton] = useState(false);
  const [closeNavigateTo, setCloseNavigateTo] = useState<string | undefined>();
  const { isLoading, login } = useAuth();
  const router = useShellRouter();

  const pendingActionRef = useRef<{
    action: LoginPostAction;
    createdAt: number;
    expiresAt: number;
    consumed: boolean;
  } | null>(null);

  const showLoginModal = useMemoizedFn((options?: LoginOpenOptions) => {
    if (options?.hideCloseButton) {
      setHideCloseButton(true);
    }
    if (options?.closeNavigateTo) {
      setCloseNavigateTo(options.closeNavigateTo);
    }
    if (options?.postLoginAction) {
      const expireMs = options.expireMs ?? 300_000;
      pendingActionRef.current = {
        action: options.postLoginAction,
        createdAt: Date.now(),
        expiresAt: Date.now() + expireMs,
        consumed: false,
      };
    }
    setLoginModalVisible(true);
  });

  const closeLoginModal = useCallback(() => {
    setLoginModalVisible(false);
    setHideCloseButton(false);
    if (closeNavigateTo) {
      router.replace(closeNavigateTo);
      setCloseNavigateTo(undefined);
    }
  }, [closeNavigateTo, router]);

  useOnboardingEntryGate({ closeLoginModal });

  const executePostAction = useMemoizedFn(async (action: LoginPostAction) => {
    switch (action.type) {
      case "navigate":
        action.replace ? router.replace(action.to) : router.push(action.to);
        break;
      case "navigate_dynamic": {
        const ctx = {
          ownerInfo: rootStore.user.ownerInfo,
          eoaAddress: LoginStore.eoaAddress,
          loginType: LoginStore.loginType,
          isLoggedIn: LoginStore.isLoggedIn,
        };
        const to = await action.toBuilder(ctx);
        if (to) {
          action.replace ? router.replace(to) : router.push(to);
        } else if (action.fallbackTo) {
          router.replace(action.fallbackTo);
        }
        break;
      }
      case "callback":
        await action.run();
        break;
      case "composed":
        for (const sub of action.actions) {
          await executePostAction(sub);
        }
        break;
    }
  });

  const handleLoginSuccess = useMemoizedFn(() => {
    setLoginModalVisible(false);
    const pending = pendingActionRef.current;
    if (pending && !pending.consumed && Date.now() < pending.expiresAt) {
      pending.consumed = true;
      executePostAction(pending.action);
    }
  });

  const loginController = useMemoizedFn(
    async (type: Web3LoginSupportedType) => {
      const provider = PROVIDER_MAP[type];
      if (!provider) {
        return;
      }

      try {
        await login(provider, { forceSelect: true });
        handleLoginSuccess();
      } catch (error) {
        log.error("[LoginModalV2] login failed", { provider, error });
      }
    },
  );

  return (
    <LoginModalContext.Provider
      value={{
        open: showLoginModal,
        loginModalVisible,
      }}
    >
      {children}
      <EmailLoginModal
        open={loginModalVisible}
        onClose={closeLoginModal}
        onSocialClick={loginController}
        loading={isLoading}
        hideCloseButton={hideCloseButton}
        closeBehavior={closeNavigateTo ? "direct" : "default"}
      />
    </LoginModalContext.Provider>
  );
};
